import useResumoCompraHook from '@/components/organisms/ResumoCompra/hook/useResumoCompraHook';
import { useSacola } from '@/hooks/useSacola';
import { useSnackbar } from '@/hooks/useSnackbar';
import { registrarEvento } from '@/services/googleAnalytics';
import {
  alterarFormaPagamentoPedido,
  realizarNovoPedido,
} from '@/services/pagamentos/pagamentoService';
import {
  construirParametrosParaRequisicaoNovoPedido,
  recuperarCombosIDs,
  recuperarProdutosFixosCombo,
} from '@/services/pagamentos/reducers';
import {
  APIResponseNovoPedido,
  MetaTrack,
} from '@/services/pagamentos/types/pagamento';
import { TipoPagamentoID } from '@/services/pagamentos/types/tipoPagamentoEnum';
import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';

export const KEY_VALOR_PEDIDO_ID_FALHA = 'pedidoIDComFalha';

export function removerValorPedidoIDComFalha() {
  return sessionStorage.removeItem(`chippuPlace@${KEY_VALOR_PEDIDO_ID_FALHA}`);
}

export default function useRevisaoPageHook() {
  const router = useRouter();
  const {
    itens,
    formaDePagamento,
    limparSacola,
    formatarFormaPagamento,
    setCompraRecente,
  } = useSacola();

  const { mostrarSnackbar } = useSnackbar();
  const { valoresResumoCompra } = useResumoCompraHook();

  const [realizandoNovoPedido, setRealizandoNovoPedido] = useState(false);

  function verificarSucessoRequisicao(
    requisicao: APIResponseNovoPedido,
  ): boolean {
    if (!requisicao.sucesso) {
      mostrarSnackbar({
        color: 'error',
        highContrast: true,
        mensagem: requisicao.mensagem ?? '',
        size: 1,
        variant: 'solid',
      });

      if (requisicao?.dados?.pedidoID) {
        sessionStorage.setItem(
          `chippuPlace@${KEY_VALOR_PEDIDO_ID_FALHA}`,
          requisicao.dados.pedidoID,
        );

        formatarFormaPagamento();
        router.back();
      }
      return false;
    }
    return true;
  }

  async function realizarCompra() {
    try {
      setRealizandoNovoPedido(true);

      const metodo = formaDePagamento?.metodo;
      if (!metodo) {
        throw new Error('Forma de pagamento não foi selecionada');
      }

      const pedidoIDComFalha = sessionStorage.getItem(
        `chippuPlace@${KEY_VALOR_PEDIDO_ID_FALHA}`,
      );

      let metaTrack: MetaTrack | undefined;
      try {
        const metaTrackData = sessionStorage.getItem('chippu@metaTrack');
        if (metaTrackData) metaTrack = JSON.parse(metaTrackData);
      } catch (error) {
        console.warn('Erro ao recuperar metaTrack:', error);
      }

      let response: APIResponseNovoPedido;

      if (pedidoIDComFalha) {
        response = await alterarFormaPagamentoPedido({
          pedidoID: pedidoIDComFalha,
          tipoPagamentoID: TipoPagamentoID[metodo],
          ...formaDePagamento?.cartao,
        });

        if (response.sucesso) {
          removerValorPedidoIDComFalha();
        }
      } else {
        response = await realizarNovoPedido({
          planoID: construirParametrosParaRequisicaoNovoPedido({
            sacola: itens,
            campo: 'opcaoSelecionadaProduto',
          }),
          comboPlanoID: [
            ...construirParametrosParaRequisicaoNovoPedido({
              sacola: itens,
              campo: 'opcaoSelecionadaPlano',
            }),
            ...recuperarProdutosFixosCombo(itens),
          ],
          comboID: recuperarCombosIDs(itens),
          tipoPagamentoID: TipoPagamentoID[metodo],
          ...formaDePagamento?.cartao,
          ...(metaTrack ? { metaTrack } : {}),
        });
      }

      if (!verificarSucessoRequisicao(response)) return;

      sessionStorage.removeItem('chippu@metaTrack');

      registrarEvento('purchase', {
        currency: 'BRL',
        value: valoresResumoCompra?.valorTotal,
        items: itens.map((item) => {
          if (item?.produto && item?.opcaoSelecionadaProduto) {
            return {
              item_id: item.opcaoSelecionadaProduto.id,
              item_brand: item.produto.nome,
              item_name: item.opcaoSelecionadaProduto.nome,
              discount: item.opcaoSelecionadaProduto.desconto,
              quantity: item.quantidade,
            };
          }
          if (item?.plano) {
            return {
              item_id: item.plano.comboID,
              item_name: item.plano.nome,
              discount: item.plano.desconto,
            };
          }
          return {};
        }),
      });

      if (metodo === 'pix') {
        const { pagamentoUrl, qrCode } = response.dados;

        if (!pagamentoUrl || !qrCode) {
          throw new Error('Dados de pix invalidos');
        }

        sessionStorage.setItem(
          `pixData@${response.dados.pedidoID}`,
          JSON.stringify({
            qrCode,
            pagamentoUrl,
          }),
        );
      } else {
        setCompraRecente([...itens]);
        limparSacola();
        formatarFormaPagamento();
      }

      const routePrefix = '/pagamento-';
      const routePath =
        metodo === 'pix' ? `pix/${response.dados.pedidoID}` : 'cartao';

      router.push(`${routePrefix}${routePath}`, { scroll: true });
    } catch (error) {
      if (error instanceof Error) {
        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? 'Ocorreu um erro ao processar sua compra',
          size: 1,
          variant: 'solid',
        });
      }
    } finally {
      setRealizandoNovoPedido(false);
    }
  }

  function alterarFormaPagamento() {
    router.back();
  }

  const mostrarAvisoPixAutomaticoAssinaturas = useMemo(() => {
    return (
      formaDePagamento?.metodo === 'pix' &&
      itens?.some((item) => item.opcaoSelecionadaProduto?.tipo === 3)
    );
  }, [itens, formaDePagamento]);

  return {
    itens,
    formaDePagamento,
    realizarCompra,
    realizandoNovoPedido,
    alterarFormaPagamento,
    mostrarAvisoPixAutomaticoAssinaturas,
  };
}

export type useRevisaoPageHookTypeReturn = ReturnType<
  typeof useRevisaoPageHook
>;
